import React, { useState } from "react";
import {
  ComposedChart,
  BarChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { ArrowRight, Info, X, Briefcase, TrendingUp, Users, DollarSign } from "lucide-react";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import ActionButton from "../utils/ActionButton";

// Load JSON data directly
import dataConfig from "./data/in-demand-jobs-wages.json";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];
const BRAND_COLORS = {
  navy: "#051c4b",
  blue: "#096faa",
  red: "#f0020e",
  gray: "#9a9fab",
  black: "#000001"
};

const wrapLabel = (label, maxChars = 14, maxLines = 3) => {
  const safeLabel = label ? String(label) : "";
  if (!safeLabel) return [""];

  const words = safeLabel.split(" ");
  const lines = [];
  let current = "";
  const ellipsisLimit = Math.max(1, maxChars - 3);

  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars) {
      current = next;
      continue;
    }

    if (current) lines.push(current);
    if (word.length > maxChars) {
      lines.push(`${word.slice(0, ellipsisLimit)}...`);
      current = "";
    } else {
      current = word;
    }
  }

  if (current) lines.push(current);
  if (lines.length > maxLines) {
    const trimmed = lines.slice(0, maxLines);
    trimmed[maxLines - 1] = `${trimmed[maxLines - 1].slice(0, ellipsisLimit)}...`;
    return trimmed;
  }

  return lines;
};

const WrappedXAxisTick = ({
  x,
  y,
  payload,
  maxChars = 14,
  lineHeight = 14,
  fontSize = 12,
  angle = 0,
  textAnchor = "middle",
  fill = BRAND_COLORS.navy
}) => {
  const chars = angle !== 0 ? maxChars * 1.5 : maxChars;
  const lines = wrapLabel(payload?.value, chars);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={textAnchor} fill={fill} fontSize={fontSize} transform={`rotate(${angle})`}>
        {lines.map((line, index) => (
          <tspan key={index} x="0" dy={index === 0 ? 16 : lineHeight} dx={angle !== 0 ? -10 : 0}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 border border-gray-200 rounded shadow-lg z-50">
        <p className="font-bold text-[#051c4b]">{label}</p>
        {payload.map((entry, index) => {
          const isCurrency = entry.name.toLowerCase().includes('wage') || entry.name.toLowerCase().includes('payroll') || entry.name.toLowerCase().includes('earnings');
          return (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {isCurrency ? '$' : ''}{entry.value.toLocaleString()}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

const InDemandJobsWages = () => {
  const [showModal, setShowModal] = useState({ open: false, title: '', content: null });

  const metricsIcons = {
    "Total Nonfarm Jobs": <Briefcase size={24} className="text-[#096faa]" />,
    "Unemployment Rate": <TrendingUp size={24} className="text-[#096faa]" />,
    "Job Openings": <Users size={24} className="text-[#096faa]" />,
    "Labor Force Participation": <DollarSign size={24} className="text-[#096faa]" />
  };

  const topOccupationsData = dataConfig.top_occupations_2026.map(item => ({
    name: item.Occupation,
    "Annual Openings": parseInt(item["Avg Annual Openings"].replace(/[^0-9]/g, '')),
    education: item["Typical Education"]
  }));

  const wageGrowthData = dataConfig.wage_growth_projection_2026.map(item => ({
    name: item["Occupational Group"],
    "Median Wage": item["Projected Median Wage"],
    "Growth Rate": typeof item["Est Annual Wage Growth"] === 'string'
      ? parseFloat(item["Est Annual Wage Growth"].replace(/[^0-9.]/g, ''))
      : item["Est Annual Wage Growth"] * 100
  }));

  const countyEarningsData = dataConfig.county_median_earnings_2025.sort((a, b) => b["Median Earnings"] - a["Median Earnings"]).slice(0, 15);

  const openModal = (title, content) => {
    setShowModal({ open: true, title, content });
  };

  return (
    <div className="bg-gradient-to-b from-slate-50 via-blue-50/70 to-white">
      <LandingSection
        data={{
          imageName: "InDemandJobsWages.webp", // Existing landing banner from similar pages
          title: "Utah's In-Demand Jobs & Wages",
          visible: "",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2 className="text-[#051c4b]">{dataConfig.title}</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Explore Utah's thriving economy through critical labor market metrics, top high-growth occupations, wage projections, and regional skill gap analysis. Comprehensive insights powered by data from <a href={dataConfig.source} target="_blank" rel="noreferrer" className="text-[#096faa] underline">Utah Jobs</a>.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* KPI Section */}
        <div className="relative overflow-hidden rounded-3xl border border-[#096faa]/20 bg-white/90 backdrop-blur p-8 shadow-[0_14px_40px_rgba(5,28,75,0.12)] mb-12 text-center">
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.35em] text-[#096faa] font-semibold">Utah Workforce</p>
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-[#051c4b]">Labor Market Snapshot</h1>
            <p className="mt-3 text-[#9a9fab] text-lg">Key metrics defining Utah's workforce resilience</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7 mb-16">
          {dataConfig.labor_market_metrics.map((metric, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden rounded-2xl border border-[#096faa]/20 bg-gradient-to-br from-white via-white to-[#f5f9ff] p-6 shadow-[0_14px_32px_rgba(5,28,75,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(5,28,75,0.16)]"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#096faa] via-[#051c4b] to-[#f0020e]" />
              <div className="absolute -right-12 -top-12 h-24 w-24 rounded-full bg-[#096faa]/12 blur-[1px]" />
              <div className="absolute -left-12 -bottom-12 h-24 w-24 rounded-full bg-[#f0020e]/10 blur-[1px]" />

              <div className="relative flex h-full flex-col gap-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    {/* <span className="inline-flex items-center rounded-full bg-[#096faa]/10 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-[#096faa] font-semibold">
                      Metric
                    </span><br/> */}
                    <h3 className="text-xl font-semibold text-[#051c4b] leading-snug whitespace-normal break-words">
                      {metric.Metric}
                    </h3>
                  </div>
                  <div className="rounded-full bg-white/90 p-3 text-[#096faa] shadow-[inset_0_0_0_1px_rgba(9,111,170,0.2)]">
                    {metricsIcons[metric.Metric] || <Info size={24} className="text-[#096faa]" />}
                  </div>
                </div>

                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="text-xl font-semibold tracking-tight text-[#051c4b] whitespace-nowrap">
                    {typeof metric.Value === 'number' && metric.Value < 1 ? (metric.Value * 100).toFixed(1) + '%' : metric.Value.toLocaleString()}
                  </p><br/>
                  <p className="min-w-[10rem] flex-1 text-left text-sm text-[#9a9fab] whitespace-normal break-words leading-snug">
                    {metric.Notes}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Top Occupations Chart */}
        <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-blue-100 shadow-[0_12px_30px_rgba(15,23,42,0.12)] mb-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <h2 className="text-2xl font-bold text-[#051c4b]">Top Occupations (2026 Projections)</h2>
            <button
              onClick={() => openModal('Top Occupations Insights', 'This chart highlights the average annual job openings for major occupations in Utah. Highlighting the need for specialized tech skills, healthcare workers, and management roles in the coming years.')}
              className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-2 bg-blue-50 text-[#096faa] rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Info size={18} />
              Insights
            </button>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={topOccupationsData} margin={{ top: 20, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
                  <XAxis
                    dataKey="name"
                    height={180}
                    tickMargin={12}
                    interval={0}
                    tick={<WrappedXAxisTick maxChars={16} angle={-35} textAnchor="end" fill={BRAND_COLORS.navy} />}
                    label={{ value: 'Occupation', position: 'insideBottom', offset: -5, fill: BRAND_COLORS.navy, fontWeight: 600 }}
                  />
                  <YAxis
                    width={90}
                    tick={{ fill: BRAND_COLORS.navy, fontSize: 12 }}
                    tickFormatter={(value) => value.toLocaleString()}
                    label={{ value: 'Annual Openings', angle: -90, position: 'insideLeft', fill: BRAND_COLORS.navy, fontWeight: 600, style: { textAnchor: 'middle' } }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
                  <Legend verticalAlign="top" height={36} />
                  <Bar dataKey="Annual Openings" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={50} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Wage Growth Projection Composed Chart */}
        <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-blue-100 shadow-[0_12px_30px_rgba(15,23,42,0.12)] mb-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <h2 className="text-2xl font-bold text-[#051c4b]">Wage Growth Projections by Group</h2>
            <button
              onClick={() => openModal('Wage Growth Insights', 'Comparing the projected median wage against the estimated annual wage growth rate (%). Data Science, AI, and IT Security currently lead the wage growth.')}
              className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-2 bg-blue-50 text-[#096faa] rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Info size={18} />
              Insights
            </button>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              <ResponsiveContainer width="100%" height={450}>
                <ComposedChart data={wageGrowthData} margin={{ top: 20, right: 10, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
                  <XAxis
                    dataKey="name"
                    height={180}
                    tickMargin={12}
                    interval={0}
                    tick={<WrappedXAxisTick maxChars={22} fontSize={12} angle={-35} textAnchor="end" fill={BRAND_COLORS.navy} />}
                    label={{ value: 'Occupational Group', position: 'insideBottom', offset: -5, fill: BRAND_COLORS.navy, fontWeight: 600 }}
                  />
                  <YAxis
                    yAxisId="left"
                    width={90}
                    tick={{ fill: BRAND_COLORS.navy, fontSize: 12 }}
                    label={{ value: 'Projected Median Wage ($)', angle: -90, position: 'insideLeft', fontWeight: 600, fill: BRAND_COLORS.navy, style: { textAnchor: 'middle' } }}
                    tickFormatter={(val) => '$' + (val / 1000).toFixed(0) + 'k'}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={90}
                    tick={{ fill: BRAND_COLORS.navy, fontSize: 12 }}
                    tickFormatter={(val) => val + '%'}
                    label={{ value: 'Growth Rate (%)', angle: 90, position: 'insideRight', fontWeight: 600, fill: BRAND_COLORS.navy, style: { textAnchor: 'middle' } }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" />
                  <Bar yAxisId="left" dataKey="Median Wage" fill="#10b981" radius={[4, 4, 0, 0]} barSize={50} />
                  <Line yAxisId="right" type="monotone" dataKey="Growth Rate" stroke="#ef4444" strokeWidth={3} dot={{ r: 6 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Industry Sector Analysis */}
        <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-blue-100 shadow-[0_12px_30px_rgba(15,23,42,0.12)] mb-12">
          <h2 className="text-2xl font-bold text-[#051c4b] mb-6">Industry Employment & Wages Overview</h2>
          <div className="overflow-x-auto">
            <div className="min-w-[1100px]">
              <ResponsiveContainer width="100%" height={500}>
                <ComposedChart data={dataConfig.industry_employment_wages} margin={{ top: 20, right: 10, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
                  <XAxis
                    dataKey="Industry Sector"
                    height={220}
                    tickMargin={12}
                    interval={0}
                    tick={<WrappedXAxisTick maxChars={18} fontSize={11} lineHeight={12} angle={-45} textAnchor="end" fill={BRAND_COLORS.navy} />}
                    label={{ value: 'Industry Sector', position: 'insideBottom', offset: -5, fontWeight: 600, fill: BRAND_COLORS.navy }}
                  />
                  <YAxis
                    yAxisId="left"
                    width={90}
                    tick={{ fill: BRAND_COLORS.navy, fontSize: 12 }}
                    label={{ value: 'Employment Count', angle: -90, position: 'insideLeft', fontWeight: 600, fill: BRAND_COLORS.navy, style: { textAnchor: 'middle' } }}
                    tickFormatter={(val) => (val / 1000).toFixed(0) + 'k'}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={90}
                    tick={{ fill: BRAND_COLORS.navy, fontSize: 12 }}
                    label={{ value: 'Avg Monthly Wage ($)', angle: 90, position: 'insideRight', fontWeight: 600, fill: BRAND_COLORS.navy, style: { textAnchor: 'middle' } }}
                    tickFormatter={(val) => '$' + (val / 1000).toFixed(0) + 'k'}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" />
                  <Bar yAxisId="left" dataKey="Employment" fill="#6366f1" radius={[2, 2, 0, 0]} />
                  <Line yAxisId="right" type="step" dataKey="Avg Monthly Wage" stroke="#f59e0b" strokeWidth={3} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* County Earnings & Urban vs Rural Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-blue-100 shadow-[0_12px_30px_rgba(15,23,42,0.12)]">
            <h2 className="text-xl font-bold text-[#051c4b] mb-6">Top 15 Counties by Median Earnings</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart layout="vertical" data={countyEarningsData} margin={{ top: 5, right: 30, left: 50, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis
                  type="number"
                  height={60}
                  tickMargin={12}
                  tickFormatter={(val) => '$' + (val / 1000).toFixed(0) + 'k'}
                  label={{ value: 'Median Earnings', position: 'insideBottom', offset: -5, fill: BRAND_COLORS.navy, fontWeight: 600 }}
                />
                <YAxis type="category" dataKey="County" width={120} tick={{ fill: BRAND_COLORS.navy, fontSize: 12 }} />
                <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} content={<CustomTooltip />} />
                <Bar dataKey="Median Earnings" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-blue-100 shadow-[0_12px_30px_rgba(15,23,42,0.12)] flex flex-col">
            <h2 className="text-xl font-bold text-[#051c4b] mb-6">Urban vs Rural Indicators</h2>
            <div className="flex-grow overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#051c4b] uppercase tracking-wider">Indicator</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#051c4b] uppercase tracking-wider">Urban</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#051c4b] uppercase tracking-wider">Rural</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dataConfig.urban_vs_rural_comparison.map((row, idx) => (
                    <tr key={idx} className={`transition-colors hover:bg-blue-50/50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#051c4b]">{row.Indicator}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#051c4b] border-l border-gray-100">{row.Urban}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#051c4b] border-l border-gray-100">{row.Rural}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Regional Skill Gaps Cards */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-[#051c4b] mb-8 text-center pt-8 border-t border-gray-200">Regional Workforce & Skill Gaps Analysis</h2>

          <div className="space-y-12">
            <div>
              <h3 className="text-2xl font-bold text-[#051c4b] mb-6 border-b-2 border-blue-500 pb-2 inline-block">High Population Counties</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dataConfig.high_population_counties.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-6 border-l-4 border-blue-500 shadow-[0_4px_16px_rgba(37,99,235,0.1)] hover:-translate-y-1 transition transform duration-300">
                    <h4 className="text-xl font-bold text-[#051c4b] mb-2">{item.County}</h4>
                    <p className="text-sm text-[#9a9fab] mb-4 h-10"><span className="font-semibold text-[#051c4b]">Insight:</span> {item.Insight}</p>
                    <div className="space-y-3 text-sm pt-4 border-t border-gray-100">
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Skill Gaps</span> <span className="font-medium text-[#051c4b] text-right w-1/2">{item["Skill Gaps"]}</span></div>
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Demand</span> <span className="font-bold text-[#096faa] px-2 py-1 bg-blue-50 rounded">{item.Demand}</span></div>
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Gap Severity</span> <span className="font-bold text-[#f0020e]">{item["Gap Severity"]}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-[#096faa] mb-6 border-b-2 border-indigo-500 pb-2 inline-block">Mid-Tier Industrial Regional Counties</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dataConfig.mid_tier_industrial_regional_counties.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-6 border-l-4 border-indigo-500 shadow-[0_4px_16px_rgba(99,102,241,0.1)] hover:-translate-y-1 transition transform duration-300">
                    <h4 className="text-xl font-bold text-[#051c4b] mb-2">{item.County}</h4>
                    <p className="text-sm text-[#9a9fab] mb-4 h-10"><span className="font-semibold text-[#051c4b]">Driver:</span> {item["Key Driver"]}</p>
                    <div className="space-y-3 text-sm pt-4 border-t border-gray-100">
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Skill Gaps</span> <span className="font-medium text-[#051c4b] text-right w-1/2">{item["Key Skill Gaps"]}</span></div>
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Demand</span> <span className="font-bold text-[#096faa] px-2 py-1 bg-indigo-50 rounded">{item["Demand Trend"]}</span></div>
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Severity</span> <span className="font-bold text-[#f0020e]">{item["Gap Severity"]}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-[#f0020e] mb-6 border-b-2 border-orange-500 pb-2 inline-block">Rural High Skill Gap Counties</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dataConfig.rural_high_skill_gap_counties.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-6 border-l-4 border-orange-500 shadow-[0_4px_16px_rgba(249,115,22,0.1)] hover:-translate-y-1 transition transform duration-300">
                    <h4 className="text-xl font-bold text-[#051c4b] mb-2">{item.County}</h4>
                    <p className="text-sm text-[#9a9fab] mb-4 h-10"><span className="font-semibold text-[#051c4b]">Risk Factor:</span> {item["Risk Factor"]}</p>
                    <div className="space-y-3 text-sm pt-4 border-t border-gray-100">
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Skill Gaps</span> <span className="font-medium text-[#051c4b] text-right w-1/2">{item["Key Skill Gaps"]}</span></div>
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Demand</span> <span className="font-bold text-[#f0020e] px-2 py-1 bg-orange-50 rounded">{item["Demand Trend"]}</span></div>
                      <div className="flex justify-between items-center"><span className="text-[#9a9fab]">Severity</span> <span className="font-bold text-[#f0020e]">{item["Gap Severity"]}</span></div>
                      <div className="text-xs text-[#f0020e] bg-orange-50 p-2 rounded mt-3 italic">{item["Unemployment / Wage Pattern"]}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Gap Severity Legend */}
          <div className="mt-12 bg-white/90 backdrop-blur rounded-2xl p-6 border border-blue-100 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
            <h4 className="text-lg font-bold text-[#051c4b] mb-3">Understanding Gap Severity</h4>
            <div className="flex flex-wrap gap-4">
              {dataConfig.gap_severity_definition.map((def, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500 hidden" />
                  <span className="font-semibold text-[#051c4b]">{def.Range}:</span>
                  <span className="text-[#9a9fab]">{def.Meaning}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* Modal */}
      {showModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold text-[#051c4b]">{showModal.title}</h3>
              <button
                onClick={() => setShowModal({ open: false, title: '', content: null })}
                className="text-[#9a9fab] hover:text-[#051c4b] transition"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 text-[#051c4b]">
              <p>{showModal.content}</p>
            </div>
          </div>
        </div>
      )}

      {/* Data Sources */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="rounded-2xl border border-[#096faa]/15 bg-white/90 backdrop-blur p-6 shadow-[0_10px_24px_rgba(5,28,75,0.08)]">
          <h4 className="text-lg font-semibold text-[#051c4b] mb-3">Data Sources</h4>
          <ul className="list-disc list-inside space-y-2 text-sm text-[#9a9fab]">
            <li>
              <a
                href="https://www.bls.gov/cew/"
                target="_blank"
                rel="noreferrer"
                className="text-[#096faa] hover:text-[#051c4b] underline"
              >
                Bureau of Labor Statistics — Quarterly Census of Employment and Wages
              </a>
            </li>
            <li>
              <a
                href="http://jobs.utah.gov/"
                target="_blank"
                rel="noreferrer"
                className="text-[#096faa] hover:text-[#051c4b] underline"
              >
                Utah Department of Workforce Services — Jobs Utah
              </a>
            </li>
            <li>
              <a
                href="https://jobs.utah.gov/utwid/industry/current/all-industries?sector=All%20Industries&areaCode=000000&areaName=Statewide&period=02032025&isHistory=false"
                target="_blank"
                rel="noreferrer"
                className="text-[#096faa] hover:text-[#051c4b] underline"
              >
                Utah Industry Data — All Industries (Statewide)
              </a>
            </li>
            <li>
              <a
                href="https://gardner.utah.edu/blog/blog-the-supply-of-and-demand-for-skills-in-utah/"
                target="_blank"
                rel="noreferrer"
                className="text-[#096faa] hover:text-[#051c4b] underline"
              >
                Kem C. Gardner Policy Institute — The Supply of and Demand for Skills in Utah
              </a>
            </li>
            <li>
              <a
                href="https://nationalskillscoalition.org/skills-mismatch/utah-skills-mismatch/"
                target="_blank"
                rel="noreferrer"
                className="text-[#096faa] hover:text-[#051c4b] underline"
              >
                National Skills Coalition — Utah Skills Mismatch
              </a>
            </li>
            <li>
              <a
                href="https://www.utahfoundation.org/"
                target="_blank"
                rel="noreferrer"
                className="text-[#096faa] hover:text-[#051c4b] underline"
              >
                Utah Foundation
              </a>
            </li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InDemandJobsWages;
